import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API, fileUrl, formatApiError } from "../lib/api";
import XIcon from "../components/XIcon";
import LegendsMark from "../components/LegendsMark";
import { SnapchatIcon, WhatsAppIcon } from "../components/BrandSocialIcons";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import {
    ArrowLeft,
    AtSign,
    BriefcaseBusiness,
    Download,
    Instagram,
    Linkedin,
    MapPin,
    Phone,
    QrCode,
    Smartphone,
} from "lucide-react";

function normalizeLink(value, type) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (type === "whatsapp" && !/^https?:\/\//i.test(raw)) {
        const phone = raw.replace(/[^\d+]/g, "").replace(/^\+/, "");
        return phone ? `https://wa.me/${phone}` : raw;
    }
    if (/^https?:\/\//i.test(raw) || /^mailto:/i.test(raw) || /^tel:/i.test(raw)) return raw;
    return `https://${raw}`;
}

export default function BusinessCardPage() {
    const { slug } = useParams();
    const { settings, settingsLoading } = useSiteSettings();
    const [card, setCard] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/business-cards/${slug}`)
            .then((r) => setCard(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, [slug]);

    const socials = useMemo(() => {
        if (!card) return [];
        return [
            { url: card.location, Icon: MapPin, label: "Location", type: "location" },
            { url: card.x_link, Icon: XIcon, label: "X", type: "x" },
            { url: card.linkedin_link, Icon: Linkedin, label: "LinkedIn", type: "linkedin" },
            { url: card.instagram_link, Icon: Instagram, label: "Instagram", type: "instagram" },
            { url: card.snapchat_link, Icon: SnapchatIcon, label: "Snapchat", type: "snapchat" },
            { url: card.whatsapp_link, Icon: WhatsAppIcon, label: "WhatsApp", type: "whatsapp" },
        ].filter((item) => item.url);
    }, [card]);

    const logoSrc = settings?.logo_path_dark || settings?.logo_path;

    if (error) {
        return (
            <section className="min-h-screen bg-white px-5 py-6 sm:px-8">
                <Logo logoSrc={logoSrc} loading={settingsLoading} />
                <div className="mx-auto mt-16 max-w-xl border border-black/10 bg-white p-6 text-center sm:p-8">
                    <h1 className="heading text-2xl text-[#002951]">Business card unavailable</h1>
                    <p className="mt-3 text-sm text-[#6B7280]">{error}</p>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#002951] px-5 py-3 text-sm font-bold text-white">
                        <ArrowLeft size={16} /> Back home
                    </Link>
                </div>
            </section>
        );
    }

    if (!card) {
        return (
            <section className="min-h-screen bg-white px-5 py-6 text-center text-[#6B7280] sm:px-8">
                <Logo logoSrc={logoSrc} loading={settingsLoading} />
                <div className="pt-24">Loading...</div>
            </section>
        );
    }

    const photo = card.profile_picture ? fileUrl(card.profile_picture) : null;
    const locationHref = card.location ? normalizeLink(card.location) : "";
    const saveQrCode = async () => {
        const filename = `${card.slug || "business-card"}-qr.png`;
        try {
            const response = await fetch(card.qr_code_url);
            const blob = await response.blob();
            const file = new File([blob], filename, { type: blob.type || "image/png" });

            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: "Business card QR code" });
                return;
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            window.open(card.qr_code_url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <section className="min-h-screen bg-[#F7F9FB] px-5 py-6 text-[#0A0A0A] sm:px-8" data-testid="public-business-card-page">
            <Logo logoSrc={logoSrc} loading={settingsLoading} />

            <div className="mx-auto max-w-3xl py-7 sm:py-10">
                <main className="business-card-animate-in space-y-7 rounded-2xl border border-[#CAD5DA]/70 bg-white px-5 py-7 shadow-xl shadow-[#002951]/8 sm:px-8 sm:py-9" style={{ animationDelay: "60ms" }}>
                    <div className="text-center">
                        <div className="business-card-scale-in mx-auto h-28 w-28 shrink-0 overflow-hidden rounded-full border-4 border-[#EFF0F1] bg-[#EFF0F1] sm:h-32 sm:w-32">
                            {photo ? (
                                <img src={photo} alt={card.full_name} className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center bg-[#002951] text-3xl font-bold text-white sm:text-4xl">
                                    {card.full_name.slice(0, 1)}
                                </div>
                            )}
                        </div>
                        <div className="business-card-animate-in mx-auto mt-5 max-w-2xl" style={{ animationDelay: "80ms" }}>
                            <h1 className="heading text-3xl font-bold leading-tight text-[#002951] sm:text-4xl">{card.full_name}</h1>
                            {card.job_title && (
                                <div className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold leading-6 text-[#0060A7] sm:text-base">
                                    <BriefcaseBusiness size={17} /> {card.job_title}
                                </div>
                            )}
                            {card.bio && card.show_bio && <p className="mt-4 max-w-2xl text-sm leading-7 text-[#4B5563]">{card.bio}</p>}
                        </div>
                    </div>

                    <div className="business-card-animate-in border-y border-black/10" style={{ animationDelay: "160ms" }}>
                        <ContactRow href={`mailto:${card.email}`} Icon={AtSign} label="Email" value={card.email} delay="200ms" />
                        {card.phone_number && <ContactRow href={`tel:${card.phone_number}`} Icon={Phone} label="Phone" value={card.phone_number} delay="250ms" />}
                        {card.mobile_number && <ContactRow href={`tel:${card.mobile_number}`} Icon={Smartphone} label="Mobile" value={card.mobile_number} delay="300ms" />}
                        {card.extension_number && <ContactRow Icon={QrCode} label="Extension" value={card.extension_number} delay="350ms" />}
                    </div>

                    {socials.length > 0 && (
                        <div className="flex flex-wrap gap-3">
                            {socials.map(({ url, Icon, label, type }, index) => (
                                <a
                                    key={label}
                                    href={type === "location" ? locationHref : normalizeLink(url, type)}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={label}
                                    className="business-card-scale-in flex h-10 w-10 items-center justify-center rounded-full border border-[#CAD5DA] text-[#002951] transition duration-300 hover:-translate-y-1 hover:border-[#0060A7] hover:bg-[#002951] hover:text-white"
                                    style={{ animationDelay: `${360 + index * 50}ms` }}
                                >
                                    <Icon size={17} />
                                </a>
                            ))}
                        </div>
                    )}

                    <div className="business-card-animate-in flex gap-3" style={{ animationDelay: "460ms" }}>
                        <a href={`${API}/business-cards/${card.slug}/vcard`} className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-[#002951] px-5 py-3 text-sm font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-[#0060A7]" data-testid="download-contact-button">
                            <Download size={17} /> Add to Contact
                        </a>
                        <button
                            type="button"
                            onClick={saveQrCode}
                            aria-label="Save QR code"
                            title="Save QR code"
                            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-[#CAD5DA] text-[#002951] transition duration-300 hover:-translate-y-0.5 hover:border-[#0060A7] hover:bg-white"
                        >
                            <QrCode size={19} />
                        </button>
                    </div>
                </main>
            </div>
        </section>
    );
}

function Logo({ logoSrc, loading }) {
    return (
        <Link to="/" className="business-card-animate-in flex w-fit items-center gap-3 text-[#002951] transition duration-300 hover:translate-x-1" aria-label="Legends home">
            {logoSrc ? (
                <img src={fileUrl(logoSrc)} alt="Legends" className="h-12 max-w-[210px] object-contain" />
            ) : loading ? (
                <span className="block h-12 w-[190px]" />
            ) : (
                <>
                    <span className="heading text-2xl font-black leading-none">LEGENDS</span>
                    <LegendsMark size={24} color="#0060A7" />
                </>
            )}
        </Link>
    );
}

function ContactRow({ href, external = false, Icon, label, value, delay = "0ms" }) {
    const content = (
        <div className="business-card-animate-in flex items-center gap-3 border-b border-black/10 py-4 text-left transition duration-300 last:border-b-0 hover:bg-[#EFF0F1]/45" style={{ animationDelay: delay }}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF0F1] text-[#0060A7]">
                <Icon size={16} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">{label}</div>
                <div className="truncate text-sm font-semibold text-[#002951]">{value}</div>
            </div>
        </div>
    );
    return href ? <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{content}</a> : content;
}
